import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';

// Components
import Topbar from '../main/Topbar';
import ProjectSidebar from '../../components/ProjectSidebar';

// Utils
import { getProjectCode } from '../../utils/auth';
import { ProjectDetail, CommonUtils, EMPTY_PROJECT_DETAIL } from '../../utils/commonUtils';
import { formatDateTimeFromApi } from '../../utils/dateTime';
import { getShopDetail, updateShop, createShopFile, deleteShopPrimary, deleteShopRewardCard, resolveShopPrimaryUrl, resolveShopRewardCardUrl, toShopPrimaryFileRequest, toShopRewardCardFileRequest } from '../../utils/shop';
import { getBrandListFlat, FlatBrandNode } from '../../utils/brand';
import { FormData, FormErrors, AddressData, ShopFormUtils } from '../../utils/shopCommon';
import {
  UploadFilePayload,
  toUploadFilePayload,
  uploadSingleFileViaPresign,
} from '../../utils/storage';
import ShopImageUploadPreview from './components/ShopImageUploadPreview';
import { 
  X, 
  Home,
  Search,
  MapPin,
  Clock,
  Phone,
  Globe,
  Save,
  ArrowLeft,
  Info,
  Image,
  Lock,
  Building2,
  Hash,
  Type,
  Calendar,
  Navigation,
  Map as MapIcon,
  Eye,
  EyeOff,
  AlertCircle,
  RefreshCw,
  History
} from 'lucide-react';

// Types are now imported from shopCommon
declare global {
  interface Window {
    naver: any;
  }
}

const ShopUpdate: React.FC = () => {
  const toTopLevelRegion = (rawRegion?: string) => {
    if (!rawRegion) return '';

    const normalized = rawRegion.trim();
    const regionMap: Record<string, string> = {
      서울특별시: '서울',
      부산광역시: '부산',
      대구광역시: '대구',
      인천광역시: '인천',
      광주광역시: '광주',
      대전광역시: '대전',
      울산광역시: '울산',
      세종특별자치시: '세종',
      경기도: '경기',
      강원특별자치도: '강원',
      강원도: '강원',
      충청북도: '충북',
      충청남도: '충남',
      전북특별자치도: '전북',
      전라북도: '전북',
      전라남도: '전남',
      경상북도: '경북',
      경상남도: '경남',
      제주특별자치도: '제주',
      제주도: '제주'
    };

    return regionMap[normalized] || normalized.replace(/특별시|광역시|특별자치시|특별자치도|자치도|도|시/g, '');
  };

  // Router hooks
  const navigate = useNavigate();
  const { shopCode } = useParams<{ shopCode: string }>();
  const location = useLocation();

  // UI state
  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('shop-list');
  const [showPassword, setShowPassword] = useState(false);
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  const [isAddressSearchOpen, setIsAddressSearchOpen] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [isNaverMapLoaded, setIsNaverMapLoaded] = useState(false);
  const [addressKeyword, setAddressKeyword] = useState('');
  const [addressResults, setAddressResults] = useState<AddressData[]>([]);
  const [isSearchingAddress, setIsSearchingAddress] = useState(false);
  const [addressSearchError, setAddressSearchError] = useState<string | null>(null);
  const [isReverseGeocoding, setIsReverseGeocoding] = useState(false);

  // Form state
  const [formData, setFormData] = useState<FormData>(ShopFormUtils.getDefaultFormData());
  const [errors, setErrors] = useState<FormErrors>({});
  const [originalData, setOriginalData] = useState<FormData | null>(null);

  // API state
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Project state
  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);
  const [shopMetadata, setShopMetadata] = useState<{
    createdAt: string;
    createdBy: string;
    updatedAt: string;
    updatedBy: string;
  } | null>(null);

  // Brand state
  const [brands, setBrands] = useState<FlatBrandNode[]>([]);
  const [isLoadingBrands, setIsLoadingBrands] = useState(false);

  // Primary image state
  const [existingPrimaryImageUrl, setExistingPrimaryImageUrl] = useState<string | null>(null);
  const [originalExistingPrimaryImageUrl, setOriginalExistingPrimaryImageUrl] = useState<string | null>(null);
  const [originalHadPrimaryImage, setOriginalHadPrimaryImage] = useState(false);
  const [pendingPrimaryImageDelete, setPendingPrimaryImageDelete] = useState(false);
  const [primaryUploadFiles, setPrimaryUploadFiles] = useState<UploadFilePayload[]>([]);
  const [primaryImagePreview, setPrimaryImagePreview] = useState('');
  const [primaryImageError, setPrimaryImageError] = useState('');
  const [isPrimaryImageUploading, setIsPrimaryImageUploading] = useState(false);

  // Reward card image state
  const [existingRewardCardImageUrl, setExistingRewardCardImageUrl] = useState<string | null>(null);
  const [originalExistingRewardCardImageUrl, setOriginalExistingRewardCardImageUrl] = useState<string | null>(null);
  const [originalHadRewardCardImage, setOriginalHadRewardCardImage] = useState(false);
  const [pendingRewardCardImageDelete, setPendingRewardCardImageDelete] = useState(false);
  const [rewardCardUploadFiles, setRewardCardUploadFiles] = useState<UploadFilePayload[]>([]);
  const [rewardCardImagePreview, setRewardCardImagePreview] = useState('');
  const [rewardCardImageError, setRewardCardImageError] = useState('');
  const [isRewardCardImageUploading, setIsRewardCardImageUploading] = useState(false);
  const mapPreviewRef = React.useRef<HTMLDivElement | null>(null);
  const mapModalRef = React.useRef<HTMLDivElement | null>(null);
  const previewMapRef = React.useRef<any>(null);
  const previewMarkerRef = React.useRef<any>(null);
  const modalMapRef = React.useRef<any>(null);
  const modalMarkerRef = React.useRef<any>(null);

  // Check if screen is mobile size
  ShopFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  // 프로젝트 정보 로드
  useEffect(() => {
    const projectInfo = ShopFormUtils.loadProjectDetail();
    if (projectInfo) {
      setProjectDetail(projectInfo);
    }
  }, []);

  // 브랜드 목록 조회
  useEffect(() => {
    const fetchBrands = async () => {
      if (!projectDetail) {
        return;
      }

      try {
        setIsLoadingBrands(true);
        const projectCode = projectDetail?.code || getProjectCode();
        if (!projectCode) {
          console.log('프로젝트 코드가 없습니다.');
          return;
        }

        console.log('평탄화된 브랜드 목록 API 호출:', { projectCode });
        const response = await getBrandListFlat(projectCode);
        console.log('평탄화된 브랜드 목록 API 응답:', response);

        if (response && response.brands) {
          setBrands(response.brands);
          console.log('설정된 브랜드 목록:', response.brands);
        } else {
          console.warn('응답에 brands 데이터가 없습니다:', response);
          setBrands([]);
        }
      } catch (err) {
        console.error('브랜드 목록 조회 오류:', err);
        // 브랜드 목록 조회 실패는 에러로 표시하지 않고 빈 배열로 설정
        setBrands([]);
      } finally {
        setIsLoadingBrands(false);
      }
    };

    if (projectDetail) {
      fetchBrands();
    }
  }, [projectDetail]);

  // 매장 상세 정보 로드 (중복 호출 방지)
  const isFetchingRef = React.useRef(false);
  
  useEffect(() => {
    const loadShopDetail = async () => {
      if (!shopCode) {
        setLoadError('매장 코드가 없습니다.');
        setIsLoading(false);
        return;
      }

      // 이미 요청 중이면 중복 호출 방지
      if (isFetchingRef.current) {
        return;
      }

      try {
        isFetchingRef.current = true;
        setIsLoading(true);
        setLoadError(null);
        
        const projectCode = getProjectCode();
        const shopDetail = await getShopDetail(projectCode, shopCode);
        
        // API 응답을 FormData 형태로 변환
        const formData: FormData = {
          outletCode: shopDetail.outletCode ?? '',
          status: shopDetail.status,
          number: shopDetail.number,
          type: shopDetail.type,
          name: shopDetail.name,
          brandCode: shopDetail.brandCode || '',
          shortName: shopDetail.shortName || '',
          salesTime: shopDetail.salesTime || '',
          comment: shopDetail.comment || '',
          intro: shopDetail.intro || '',
          phoneNumber: shopDetail.phoneNumber,
          ownerPhoneNumber: shopDetail.ownerPhoneNumber || '',
          homepage: shopDetail.homepage || '',
          postalCode: shopDetail.postalCode || '',
          regionCode: shopDetail.regionCode || '',
          addressRoad: shopDetail.addressRoad,
          addressDetail: shopDetail.addressDetail,
          lat: shopDetail.lat,
          lon: shopDetail.lon,
          loginPassword: shopDetail.loginPassword || ''
        };

        setFormData(formData);
        setOriginalData(formData);
        
        // 메타데이터 저장
        setShopMetadata({
          createdAt: shopDetail.createdAt,
          createdBy: shopDetail.createdBy,
          updatedAt: shopDetail.updatedAt,
          updatedBy: shopDetail.updatedBy
        });

        const [resolvedPrimaryImage, resolvedRewardCardImage] = await Promise.all([
          resolveShopPrimaryUrl(
            projectCode,
            shopCode,
            shopDetail.primaryVersion,
            shopDetail.updatedAt
          ).catch((err) => {
            console.warn('매장 대표 이미지 URL 조회 실패:', shopCode, err);
            return null;
          }),
          resolveShopRewardCardUrl(
            projectCode,
            shopCode,
            shopDetail.rewardCardVersion,
            shopDetail.updatedAt
          ).catch((err) => {
            console.warn('매장 리워드 카드 이미지 URL 조회 실패:', shopCode, err);
            return null;
          }),
        ]);

        setExistingPrimaryImageUrl(resolvedPrimaryImage);
        setOriginalExistingPrimaryImageUrl(resolvedPrimaryImage);
        setOriginalHadPrimaryImage(!!resolvedPrimaryImage);
        setPendingPrimaryImageDelete(false);
        setPrimaryUploadFiles([]);
        setPrimaryImagePreview('');
        setPrimaryImageError('');

        setExistingRewardCardImageUrl(resolvedRewardCardImage);
        setOriginalExistingRewardCardImageUrl(resolvedRewardCardImage);
        setOriginalHadRewardCardImage(!!resolvedRewardCardImage);
        setPendingRewardCardImageDelete(false);
        setRewardCardUploadFiles([]);
        setRewardCardImagePreview('');
        setRewardCardImageError('');
      } catch (error: any) {
        console.error('매장 상세 정보 로드 오류:', error);
        const displayMessage = CommonUtils.getNetworkErrorMessage(error);
        setLoadError(displayMessage);
      } finally {
        setIsLoading(false);
        isFetchingRef.current = false;
      }
    };

    loadShopDetail();
  }, [shopCode]);

  useEffect(() => {
    const clientId = import.meta.env.VITE_NAVER_MAP_CLIENT_ID;

    if (!clientId) return;

    if (window.naver?.maps) {
      setIsNaverMapLoaded(true);
      return;
    }

    const existingScript = document.querySelector<HTMLScriptElement>('script[data-naver-map-script="true"]');
    if (existingScript) {
      const onLoad = () => setIsNaverMapLoaded(true);
      existingScript.addEventListener('load', onLoad);
      return () => existingScript.removeEventListener('load', onLoad);
    }

    const script = document.createElement('script');
    script.src = `https://oapi.map.naver.com/openapi/v3/maps.js?ncpKeyId=${clientId}&submodules=geocoder`;
    script.async = true;
    script.defer = true;
    script.dataset.naverMapScript = 'true';
    script.onload = () => setIsNaverMapLoaded(true);
    document.head.appendChild(script);
  }, []);

  useEffect(() => {
    if (!isNaverMapLoaded || !mapPreviewRef.current) return;

    const naver = window.naver;
    const lat = formData.lat || 37.5665;
    const lon = formData.lon || 126.978;
    const center = new naver.maps.LatLng(lat, lon);

    if (!previewMapRef.current) {
      previewMapRef.current = new naver.maps.Map(mapPreviewRef.current, { center, zoom: 16 });
    } else {
      previewMapRef.current.setCenter(center);
    }

    if (!previewMarkerRef.current) {
      previewMarkerRef.current = new naver.maps.Marker({ position: center, map: previewMapRef.current });
    } else {
      previewMarkerRef.current.setPosition(center);
    }
  }, [isNaverMapLoaded, formData.lat, formData.lon]);

  useEffect(() => {
    if (!isMapModalOpen || !isNaverMapLoaded || !mapModalRef.current) return;

    const naver = window.naver;
    const lat = formData.lat || 37.5665;
    const lon = formData.lon || 126.978;
    const center = new naver.maps.LatLng(lat, lon);

    modalMapRef.current = new naver.maps.Map(mapModalRef.current, { center, zoom: 16 });
    modalMarkerRef.current = new naver.maps.Marker({ position: center, map: modalMapRef.current });

    const clickListener = naver.maps.Event.addListener(modalMapRef.current, 'click', (e: any) => {
      const newLat = Number(e.coord.y.toFixed(6));
      const newLon = Number(e.coord.x.toFixed(6));
      const nextPosition = new naver.maps.LatLng(newLat, newLon);

      modalMarkerRef.current.setPosition(nextPosition);
      setFormData(prev => ({ ...prev, lat: newLat, lon: newLon }));
    });

    return () => {
      naver.maps.Event.removeListener(clickListener);
      modalMarkerRef.current = null;
      modalMapRef.current = null;
    };
  }, [isMapModalOpen, isNaverMapLoaded, formData.lat, formData.lon]);

  // Track changes
  useEffect(() => {
    if (!originalData) {
      setHasChanges(false);
      return;
    }
    const hasFormChanges = JSON.stringify(formData) !== JSON.stringify(originalData);
    const hasPrimaryFileChanges = primaryUploadFiles.length > 0 || pendingPrimaryImageDelete;
    const hasRewardCardFileChanges =
      rewardCardUploadFiles.length > 0 || pendingRewardCardImageDelete;
    setHasChanges(hasFormChanges || hasPrimaryFileChanges || hasRewardCardFileChanges);
  }, [
    formData,
    originalData,
    primaryUploadFiles,
    pendingPrimaryImageDelete,
    rewardCardUploadFiles,
    pendingRewardCardImageDelete,
  ]);

  const displayPrimaryImageUrl =
    pendingPrimaryImageDelete && primaryUploadFiles.length === 0
      ? null
      : primaryImagePreview || existingPrimaryImageUrl;

  const displayRewardCardImageUrl =
    pendingRewardCardImageDelete && rewardCardUploadFiles.length === 0
      ? null
      : rewardCardImagePreview || existingRewardCardImageUrl;

  const handleValidatedPrimaryImage = async ({
    file,
    dataUrl,
  }: {
    file: File;
    dataUrl: string;
  }) => {
    const projectCode = projectDetail?.code || getProjectCode();
    if (!projectCode) {
      setPrimaryImageError('프로젝트 정보가 없습니다.');
      return;
    }

    setPrimaryUploadFiles([]);
    setPrimaryImagePreview('');
    setExistingPrimaryImageUrl(null);
    setPrimaryImageError('');
    setIsPrimaryImageUploading(true);

    try {
      const { upload } = await uploadSingleFileViaPresign(file, {
        domain: 'SHOP',
        projectCode,
      });
      setPrimaryUploadFiles([toUploadFilePayload(upload)]);
      setPrimaryImagePreview(dataUrl);
      setPendingPrimaryImageDelete(false);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : '이미지 업로드 중 오류가 발생했습니다';
      setPrimaryImageError(message);
      setExistingPrimaryImageUrl(originalExistingPrimaryImageUrl);
      setPrimaryImagePreview('');
      setPrimaryUploadFiles([]);
    } finally {
      setIsPrimaryImageUploading(false);
    }
  };

  const handleValidatedRewardCardImage = async ({
    file,
    dataUrl,
  }: {
    file: File;
    dataUrl: string;
  }) => {
    const projectCode = projectDetail?.code || getProjectCode();
    if (!projectCode) {
      setRewardCardImageError('프로젝트 정보가 없습니다.');
      return;
    }

    setRewardCardUploadFiles([]);
    setRewardCardImagePreview('');
    setExistingRewardCardImageUrl(null);
    setRewardCardImageError('');
    setIsRewardCardImageUploading(true);

    try {
      const { upload } = await uploadSingleFileViaPresign(file, {
        domain: 'SHOP',
        projectCode,
      });
      setRewardCardUploadFiles([toUploadFilePayload(upload)]);
      setRewardCardImagePreview(dataUrl);
      setPendingRewardCardImageDelete(false);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : '이미지 업로드 중 오류가 발생했습니다';
      setRewardCardImageError(message);
      setExistingRewardCardImageUrl(originalExistingRewardCardImageUrl);
      setRewardCardImagePreview('');
      setRewardCardUploadFiles([]);
    } finally {
      setIsRewardCardImageUploading(false);
    }
  };

  const handlePrimaryImageLoadError = () => {
    setExistingPrimaryImageUrl(null);
    setOriginalExistingPrimaryImageUrl(null);
    setOriginalHadPrimaryImage(false);
    setPrimaryImagePreview('');
    setPendingPrimaryImageDelete(false);
    setPrimaryUploadFiles([]);
  };

  const handleRewardCardImageLoadError = () => {
    setExistingRewardCardImageUrl(null);
    setOriginalExistingRewardCardImageUrl(null);
    setOriginalHadRewardCardImage(false);
    setRewardCardImagePreview('');
    setPendingRewardCardImageDelete(false);
    setRewardCardUploadFiles([]);
  };

  const handleRemovePrimaryImage = () => {
    if (primaryUploadFiles.length > 0) {
      setPrimaryUploadFiles([]);
      setPrimaryImagePreview('');
      if (pendingPrimaryImageDelete || !originalHadPrimaryImage) {
        setExistingPrimaryImageUrl(null);
      } else {
        setExistingPrimaryImageUrl(originalExistingPrimaryImageUrl);
        setPendingPrimaryImageDelete(false);
      }
      setPrimaryImageError('');
      return;
    }

    if (originalHadPrimaryImage && !pendingPrimaryImageDelete) {
      setPendingPrimaryImageDelete(true);
      setExistingPrimaryImageUrl(null);
      setPrimaryImagePreview('');
      setPrimaryImageError('');
    }
  };

  const handleRemoveRewardCardImage = () => {
    if (rewardCardUploadFiles.length > 0) {
      setRewardCardUploadFiles([]);
      setRewardCardImagePreview('');
      if (pendingRewardCardImageDelete || !originalHadRewardCardImage) {
        setExistingRewardCardImageUrl(null);
      } else {
        setExistingRewardCardImageUrl(originalExistingRewardCardImageUrl);
        setPendingRewardCardImageDelete(false);
      }
      setRewardCardImageError('');
      return;
    }

    if (originalHadRewardCardImage && !pendingRewardCardImageDelete) {
      setPendingRewardCardImageDelete(true);
      setExistingRewardCardImageUrl(null);
      setRewardCardImagePreview('');
      setRewardCardImageError('');
    }
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    ShopFormUtils.handleInputChange(e, setFormData, setErrors);
  };

  // Validate form
  const validateForm = () => {
    const newErrors = ShopFormUtils.validateUpdateForm(formData);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isPrimaryImageUploading || isRewardCardImageUploading) {
      alert('이미지 업로드가 진행 중입니다. 잠시 후 다시 시도해주세요.');
      return;
    }

    const hasFormChanges =
      !!originalData && JSON.stringify(formData) !== JSON.stringify(originalData);
    const hasPrimaryFileChanges =
      primaryUploadFiles.length > 0 || pendingPrimaryImageDelete;
    const hasRewardCardFileChanges =
      rewardCardUploadFiles.length > 0 || pendingRewardCardImageDelete;

    if (!hasFormChanges && !hasPrimaryFileChanges && !hasRewardCardFileChanges) {
      return;
    }

    if (hasFormChanges && !validateForm()) {
      return;
    }

    if (!shopCode) {
      setSubmitError('매장 코드가 없습니다.');
      return;
    }

    const projectCode = getProjectCode();
    if (!projectCode) {
      setSubmitError('프로젝트 정보를 찾을 수 없습니다.');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    let formSaved = false;

    try {
      if (hasFormChanges) {
        const updateData = {
          outletCode: formData.outletCode,
          status: formData.status,
          number: formData.number,
          type: formData.type,
          name: formData.name,
          brandCode: formData.brandCode || undefined,
          shortName: formData.shortName || undefined,
          salesTime: formData.salesTime || undefined,
          comment: formData.comment || undefined,
          intro: formData.intro || undefined,
          phoneNumber: formData.phoneNumber,
          ownerPhoneNumber: formData.ownerPhoneNumber || undefined,
          homepage: formData.homepage || undefined,
          postalCode: formData.postalCode || undefined,
          regionCode: formData.regionCode || undefined,
          addressRoad: formData.addressRoad,
          addressDetail: formData.addressDetail,
          lat: formData.lat,
          lon: formData.lon,
          loginPassword: formData.loginPassword || undefined,
        };

        const result = await updateShop(projectCode, shopCode, updateData);
        if (!result.success) {
          throw new Error(result.message || '매장 수정에 실패했습니다.');
        }
        setOriginalData({ ...formData });
        formSaved = true;
      }

      const processPrimaryFiles = async () => {
        const hasNewUpload = primaryUploadFiles.length > 0;
        const shouldDelete =
          pendingPrimaryImageDelete || (hasNewUpload && originalHadPrimaryImage);

        if (shouldDelete) {
          await deleteShopPrimary(projectCode, shopCode);
        }

        for (const uploadFile of primaryUploadFiles) {
          await createShopFile(projectCode, shopCode, toShopPrimaryFileRequest(uploadFile));
        }

        setPrimaryUploadFiles([]);
        setPendingPrimaryImageDelete(false);
        if (hasNewUpload) {
          setOriginalHadPrimaryImage(true);
          setExistingPrimaryImageUrl(null);
          setOriginalExistingPrimaryImageUrl(null);
        } else {
          setOriginalHadPrimaryImage(false);
          setPrimaryImagePreview('');
          setExistingPrimaryImageUrl(null);
          setOriginalExistingPrimaryImageUrl(null);
        }
      };

      const processRewardCardFiles = async () => {
        const hasNewUpload = rewardCardUploadFiles.length > 0;
        const shouldDelete =
          pendingRewardCardImageDelete || (hasNewUpload && originalHadRewardCardImage);

        if (shouldDelete) {
          await deleteShopRewardCard(projectCode, shopCode);
        }

        for (const uploadFile of rewardCardUploadFiles) {
          await createShopFile(
            projectCode,
            shopCode,
            toShopRewardCardFileRequest(uploadFile)
          );
        }

        setRewardCardUploadFiles([]);
        setPendingRewardCardImageDelete(false);
        if (hasNewUpload) {
          setOriginalHadRewardCardImage(true);
          setExistingRewardCardImageUrl(null);
          setOriginalExistingRewardCardImageUrl(null);
        } else {
          setOriginalHadRewardCardImage(false);
          setRewardCardImagePreview('');
          setExistingRewardCardImageUrl(null);
          setOriginalExistingRewardCardImageUrl(null);
        }
      };

      if (hasPrimaryFileChanges || hasRewardCardFileChanges) {
        try {
          if (hasPrimaryFileChanges) {
            await processPrimaryFiles();
          }
          if (hasRewardCardFileChanges) {
            await processRewardCardFiles();
          }
        } catch (fileError) {
          if (formSaved) {
            alert(
              '매장 정보는 저장되었습니다.\n이미지 파일 처리에 실패했습니다. 다시 시도해 주세요.'
            );
            return;
          }
          throw fileError;
        }
      }

      setHasChanges(false);
      alert('매장 정보가 성공적으로 수정되었습니다.');
      const searchParams = new URLSearchParams(location.search);
      const page = searchParams.get('page') || '0';
      const size = searchParams.get('size') || '20';
      navigate(`/shops?page=${page}&size=${size}`);
    } catch (error: unknown) {
      console.error('매장 수정 오류:', error);
      const displayMessage =
        error instanceof Error
          ? error.message
          : CommonUtils.getNetworkErrorMessage(error) ||
            '매장 수정 중 오류가 발생했습니다.';
      setSubmitError(displayMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form to original data
  const handleReset = () => {
    if (originalData) {
      setFormData({ ...originalData });
    }
    setExistingPrimaryImageUrl(originalExistingPrimaryImageUrl);
    setOriginalHadPrimaryImage(!!originalExistingPrimaryImageUrl);
    setPendingPrimaryImageDelete(false);
    setPrimaryUploadFiles([]);
    setPrimaryImagePreview('');
    setPrimaryImageError('');

    setExistingRewardCardImageUrl(originalExistingRewardCardImageUrl);
    setOriginalHadRewardCardImage(!!originalExistingRewardCardImageUrl);
    setPendingRewardCardImageDelete(false);
    setRewardCardUploadFiles([]);
    setRewardCardImagePreview('');
    setRewardCardImageError('');

    setErrors({});
    setHasChanges(false);
    setShowResetConfirm(false);
  };

  // Mock address search
  const handleAddressSearch = () => {
    setIsAddressSearchOpen(true);
    setAddressSearchError(null);
    setAddressResults([]);
  };

  // Mock map location selection
  const handleMapSelection = () => {
    setIsMapModalOpen(true);
  };

  const handleSearchAddressByKeyword = () => {
    if (!isNaverMapLoaded) {
      setAddressSearchError('네이버 지도 SDK 로딩 중입니다. 잠시 후 다시 시도해 주세요.');
      return;
    }

    const keyword = addressKeyword.trim();
    if (!keyword) {
      setAddressSearchError('검색어를 입력해 주세요.');
      return;
    }

    setIsSearchingAddress(true);
    setAddressSearchError(null);

    window.naver.maps.Service.geocode({ query: keyword }, (status: any, response: any) => {
      setIsSearchingAddress(false);

      if (status !== window.naver.maps.Service.Status.OK) {
        setAddressSearchError('주소 검색에 실패했습니다.');
        setAddressResults([]);
        return;
      }

      const items = response.v2?.addresses || [];
      if (!items.length) {
        setAddressSearchError('검색 결과가 없습니다.');
        setAddressResults([]);
        return;
      }

      const mappedResults: AddressData[] = items.slice(0, 10).map((item: any) => {
        const sidoName =
          item.addressElements?.find((element: any) => element.types?.includes('SIDO'))?.longName ||
          item.addressElements?.find((element: any) => element.types?.includes('SIDO'))?.shortName ||
          item.addressElements?.[0]?.longName ||
          '';

        return {
          postalCode: item.zipCode || '',
          road: item.roadAddress || item.jibunAddress || '',
          regionCode: toTopLevelRegion(sidoName),
          lat: Number(item.y),
          lon: Number(item.x)
        };
      });
      setAddressResults(mappedResults);
    });
  };

  const handleReverseGeocode = (lat: number, lon: number) => {
    if (!isNaverMapLoaded) return;

    setIsReverseGeocoding(true);

    const coords = new window.naver.maps.Point(lon, lat);
    window.naver.maps.Service.reverseGeocode(
      {
        coords,
        orders: [
          window.naver.maps.Service.OrderType.ROAD_ADDR,
          window.naver.maps.Service.OrderType.ADDR
        ].join(',')
      },
      (status: any, response: any) => {
        setIsReverseGeocoding(false);

        if (status !== window.naver.maps.Service.Status.OK) return;

        const results = response.v2?.results || [];
        const roadResult = results.find((result: any) => result.name === 'roadaddr');
        const addrResult = results.find((result: any) => result.name === 'addr');
        const picked = roadResult || addrResult;

        if (!picked) return;

        const region = picked.region || {};
        const land = picked.land || {};
        const landNo = land.number1 ? `${land.number1}${land.number2 ? `-${land.number2}` : ''}` : '';
        // 도로명 주소는 시/도 + 시/군/구 + 도로명 + 건물번호 형태로 구성한다.
        const road = [region.area1?.name, region.area2?.name, land.name, landNo].filter(Boolean).join(' ');

        setFormData(prev => ({
          ...prev,
          addressRoad: road || prev.addressRoad,
          regionCode: toTopLevelRegion(region.area1?.name) || prev.regionCode
        }));
      }
    );
  };

  // Mock address selection
  const selectAddress = (address: AddressData) => {
    setFormData(prev => ({
      ...prev,
      postalCode: address.postalCode,
      addressRoad: address.road,
      regionCode: address.regionCode,
      lat: address.lat,
      lon: address.lon
    }));
    setIsAddressSearchOpen(false);
    setAddressKeyword(address.road);
    setAddressResults([]);
  };

  // 메뉴 아이템은 ProjectSidebar에서 관리

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-gray-400 animate-spin mx-auto mb-2" />
          <p className="text-gray-600">매장 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md">
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
              <X className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-lg font-medium text-red-900 mb-2">오류 발생</h3>
            <p className="text-red-700 mb-4">{loadError}</p>
            <div className="flex gap-2 justify-center">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                다시 시도
              </button>
              <button
                onClick={() => navigate('/shops')}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                목록으로
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      <ProjectSidebar
        isDesktopSidebarOpen={isDesktopSidebarOpen}
        setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
        isMobileSidebarOpen={isMobileSidebarOpen}
        setIsMobileSidebarOpen={setIsMobileSidebarOpen}
        activeMenu={activeMenu}
        setActiveMenu={setActiveMenu}
        projectDetail={projectDetail || EMPTY_PROJECT_DETAIL}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Bar */}
        <Topbar 
          title="매장 수정" 
          subtitle={projectDetail?.name || '프로젝트를 선택해주세요'}
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          isProfileDropdownOpen={isProfileDropdownOpen}
          setIsProfileDropdownOpen={setIsProfileDropdownOpen}
          showMobileLogo={true}
          projectDetail={projectDetail || undefined}
        />

        {/* Edit Store Content */}
        <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <div className="max-w-6xl mx-auto">
            {/* Back Button and Status */}
            <div className="flex items-center justify-between mb-6">
              <button 
                onClick={() => navigate('/shops')}
                className="flex items-center text-gray-600 hover:text-gray-900"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                매장 목록으로
              </button>
              
              {hasChanges && (
                <span className="flex items-center text-sm text-orange-600 bg-orange-50 px-3 py-1 rounded-full">
                  <AlertCircle className="w-4 h-4 mr-1" />
                  수정사항 있음
                </span>
              )}
            </div>

            {/* Store Info Card */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-2">
                    {formData.name} ({formData.outletCode})
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600">
                    {shopMetadata && (
                      <>
                        <div className="flex items-center">
                          <History className="w-4 h-4 mr-2" />
                          생성: {formatDateTimeFromApi(shopMetadata.createdAt) || shopMetadata.createdAt} ({shopMetadata.createdBy})
                        </div>
                        <div className="flex items-center">
                          <RefreshCw className="w-4 h-4 mr-2" />
                          수정: {formatDateTimeFromApi(shopMetadata.updatedAt) || shopMetadata.updatedAt} ({shopMetadata.updatedBy})
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Error Message */}
            {submitError && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center">
                  <X className="w-5 h-5 text-red-600 mr-2" />
                  <p className="text-red-700">{submitError}</p>
                </div>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit}>
              {/* Basic Information */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Home className="w-5 h-5 mr-2" />
                  기본 정보
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Brand */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      브랜드
                    </label>
                    <select
                      name="brandCode"
                      value={formData.brandCode || ''}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border ${errors.brandCode ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      disabled={isLoadingBrands}
                    >
                      <option value="">브랜드를 선택하세요</option>
                      {brands.map((brand) => {
                        // Non-breaking space (공백이 축약되지 않도록)
                        const nbsp = '\u00A0';
                        // depth에 따라 들여쓰기와 "ㄴ" 기호 추가
                        const indent = (nbsp + nbsp).repeat(brand.depth);
                        const prefix = brand.depth > 0 ? 'ㄴ ' : '';
                        const displayName = `${indent}${prefix}${brand.name} (${brand.code})`;
                        
                        return (
                          <option key={brand.code} value={brand.code}>
                            {displayName}
                          </option>
                        );
                      })}
                    </select>
                    {errors.brandCode && <p className="mt-1 text-xs text-red-500">{errors.brandCode}</p>}
                  </div>

                  {/* Store Code */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      매장 코드
                    </label>
                    <input
                      type="text"
                      name="outletCode"
                      value={formData.outletCode}
                      onChange={handleInputChange}
                      maxLength={20}
                      className={`w-full px-3 py-2 border ${errors.outletCode ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="SHOP000001"
                    />
                    {errors.outletCode && <p className="mt-1 text-xs text-red-500">{errors.outletCode}</p>}
                  </div>

                  {/* Status */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      상태 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="ACTIVE">활성</option>
                      <option value="INACTIVE">비활성</option>
                      <option value="PENDING">대기중</option>
                      <option value="TERMINATED">종료</option>
                      <option value="DELETED">삭제</option>
                    </select>
                  </div>

                  {/* Store Number */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      매장 번호 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="number"
                      value={formData.number}
                      onChange={handleInputChange}
                      className={`w-full px-3 py-2 border ${errors.number ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [-moz-appearance:textfield]`}
                      placeholder="1"
                    />
                    {errors.number && <p className="mt-1 text-xs text-red-500">{errors.number}</p>}
                  </div>

                  {/* Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      타입 <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="franchise">가맹점</option>
                      <option value="company">직영점</option>
                      <option value="popup">팝업</option>
                      <option value="shopping_mall">쇼핑몰</option>
                    </select>
                  </div>

                  {/* Store Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      매장명 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      maxLength={30}
                      className={`w-full px-3 py-2 border ${errors.name ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="강남 플래그십 스토어"
                    />
                    {errors.name && <p className="mt-1 text-xs text-red-500">{errors.name}</p>}
                  </div>

                  {/* Short Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      축약명
                    </label>
                    <input
                      type="text"
                      name="shortName"
                      value={formData.shortName}
                      onChange={handleInputChange}
                      maxLength={10}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="강남점"
                    />
                  </div>

                  {/* Sales Time */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      영업시간
                    </label>
                    <input
                      type="text"
                      name="salesTime"
                      value={formData.salesTime}
                      onChange={handleInputChange}
                      maxLength={200}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="평일 08:00-22:00, 주말 10:00-21:00"
                    />
                  </div>
                </div>

                {/* Comment */}
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    짧은 소개
                  </label>
                  <input
                    type="text"
                    name="comment"
                    value={formData.comment}
                    onChange={handleInputChange}
                      maxLength={100}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="강남역 1번 출구 바로 앞"
                  />
                </div>

                {/* Intro */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    상세 소개
                  </label>
                  <textarea
                    name="intro"
                    value={formData.intro}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="매장에 대한 상세한 소개를 입력하세요..."
                  />
                </div>
              </div>

              {/* Contact Information */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Phone className="w-5 h-5 mr-2" />
                  연락처 정보
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Store Phone */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      매장 전화번호 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      maxLength={20}
                      className={`w-full px-3 py-2 border ${errors.phoneNumber ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="0212345678"
                    />
                    {errors.phoneNumber && <p className="mt-1 text-xs text-red-500">{errors.phoneNumber}</p>}
                  </div>

                  {/* Owner Phone */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      점주 연락처
                    </label>
                    <input
                      type="tel"
                      name="ownerPhoneNumber"
                      value={formData.ownerPhoneNumber}
                      onChange={handleInputChange}
                      maxLength={20}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="01012345678"
                    />
                  </div>

                  {/* Homepage */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      홈페이지
                    </label>
                    <input
                      type="text"
                      name="homepage"
                      value={formData.homepage}
                      onChange={handleInputChange}
                      maxLength={250}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="https://store.example.com"
                    />
                  </div>
                </div>
              </div>

              {/* Location Information */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <MapPin className="w-5 h-5 mr-2" />
                  위치 정보
                </h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Postal Code */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      우편번호
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        name="postalCode"
                        value={formData.postalCode}
                        onChange={handleInputChange}
                        maxLength={10}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="06234"
                      />
                      <button
                        type="button"
                        onClick={handleAddressSearch}
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                      >
                        주소 검색
                      </button>
                    </div>
                  </div>

                  {/* Region Code */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      지역 코드
                    </label>
                    <input
                      type="text"
                      name="regionCode"
                      value={formData.regionCode}
                      onChange={handleInputChange}
                      maxLength={20}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="서울"
                    />
                  </div>

                  {/* Road Address */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      도로명 주소 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="addressRoad"
                      value={formData.addressRoad}
                      onChange={handleInputChange}
                      maxLength={200}
                      className={`w-full px-3 py-2 border ${errors.addressRoad ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="서울특별시 강남구 테헤란로 123"
                    />
                    {errors.addressRoad && <p className="mt-1 text-xs text-red-500">{errors.addressRoad}</p>}
                  </div>

                  {/* Detail Address */}
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      상세 주소 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="addressDetail"
                      value={formData.addressDetail}
                      onChange={handleInputChange}
                      maxLength={200}
                      className={`w-full px-3 py-2 border ${errors.addressDetail ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="강남빌딩 1층"
                    />
                    {errors.addressDetail && <p className="mt-1 text-xs text-red-500">{errors.addressDetail}</p>}
                  </div>

                  {/* Coordinates */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      위도 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="lat"
                      value={formData.lat || ''}
                      onChange={handleInputChange}
                      step="0.000001"
                      className={`w-full px-3 py-2 border ${errors.location ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="37.498095"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      경도 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      name="lon"
                      value={formData.lon || ''}
                      onChange={handleInputChange}
                      step="0.000001"
                      className={`w-full px-3 py-2 border ${errors.location ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="127.027610"
                    />
                  </div>

                  {errors.location && (
                    <div className="md:col-span-2">
                      <p className="text-xs text-red-500">{errors.location}</p>
                    </div>
                  )}

                  {/* Map Button */}
                  <div className="md:col-span-2">
                    <button
                      type="button"
                      onClick={handleMapSelection}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center justify-center"
                    >
                      <MapIcon className="w-5 h-5 mr-2" />
                      지도에서 위치 선택
                    </button>
                  </div>

                  {/* Map Preview Area */}
                  <div className="md:col-span-2">
                    {!import.meta.env.VITE_NAVER_MAP_CLIENT_ID ? (
                      <div className="h-64 bg-yellow-50 rounded-lg flex items-center justify-center border border-yellow-300">
                        <p className="text-sm text-yellow-700">
                          `.env`에 `VITE_NAVER_MAP_CLIENT_ID`를 설정하면 지도가 표시됩니다.
                        </p>
                      </div>
                    ) : (
                      <div ref={mapPreviewRef} className="h-64 rounded-lg border border-gray-300" />
                    )}
                  </div>
                </div>
              </div>

              {/* Image Upload */}
              <div className="mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Image className="w-5 h-5 mr-2" />
                  이미지
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ShopImageUploadPreview
                    variant="square"
                    label="대표 이미지"
                    hint="권장 비율은 1:1입니다. 다른 비율도 업로드할 수 있으며, 이후 크롭됩니다."
                    imageUrl={displayPrimaryImageUrl}
                    isUploading={isPrimaryImageUploading}
                    error={primaryImageError}
                    onValidatedImage={handleValidatedPrimaryImage}
                    onRemoveImage={handleRemovePrimaryImage}
                    onImageError={setPrimaryImageError}
                    onImageLoadError={handlePrimaryImageLoadError}
                  />
                  <ShopImageUploadPreview
                    variant="reward-card"
                    label="리워드 카드"
                    hint="권장 비율은 16:9입니다. 다른 비율도 업로드할 수 있으며, 이후 크롭됩니다."
                    imageUrl={displayRewardCardImageUrl}
                    isUploading={isRewardCardImageUploading}
                    error={rewardCardImageError}
                    onValidatedImage={handleValidatedRewardCardImage}
                    onRemoveImage={handleRemoveRewardCardImage}
                    onImageError={setRewardCardImageError}
                    onImageLoadError={handleRewardCardImageLoadError}
                  />
                </div>
              </div>

              {/* Login Information */}
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Lock className="w-5 h-5 mr-2" />
                  로그인 정보
                </h2>
                
                <div className="max-w-md">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    로그인 비밀번호 <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      name="loginPassword"
                      value={formData.loginPassword}
                      onChange={handleInputChange}
                      maxLength={20}
                      className={`w-full px-3 py-2 pr-10 border ${errors.loginPassword ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      placeholder="로그인 비밀번호를 입력하세요"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5 text-gray-400" />
                      ) : (
                        <Eye className="w-5 h-5 text-gray-400" />
                      )}
                    </button>
                  </div>
                  {errors.loginPassword && <p className="mt-1 text-xs text-red-500">{errors.loginPassword}</p>}
                  <p className="mt-2 text-xs text-gray-500">
                    로그인 비밀번호는 필수 입력 항목입니다.
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-between">
                <button
                  type="button"
                  onClick={() => setShowResetConfirm(true)}
                  className={`px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center ${!hasChanges && 'opacity-50 cursor-not-allowed'}`}
                  disabled={!hasChanges}
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  초기화
                </button>
                
                <div className="flex space-x-3">
                  <button
                    type="button"
                    onClick={() => window.history.back()}
                    className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    취소
                  </button>
                  <button
                    type="submit"
                    className={`px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center ${(!hasChanges || isSubmitting) && 'opacity-50 cursor-not-allowed'}`}
                    disabled={!hasChanges || isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                        저장 중...
                      </>
                    ) : (
                      <>
                        <Save className="w-5 h-5 mr-2" />
                        변경사항 저장
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </main>
      </div>

      {/* Address Search Modal */}
      {isAddressSearchOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold">주소 검색</h3>
              <button
                onClick={() => setIsAddressSearchOpen(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              <div className="flex gap-2 mb-4">
                <input
                  type="text"
                  value={addressKeyword}
                  onChange={(e) => setAddressKeyword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSearchAddressByKeyword();
                    }
                  }}
                  placeholder="도로명 또는 지번 주소 검색"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={handleSearchAddressByKeyword}
                  disabled={isSearchingAddress}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-blue-400"
                >
                  {isSearchingAddress ? '검색 중...' : '검색'}
                </button>
              </div>
              
              {addressSearchError && (
                <p className="mb-3 text-sm text-red-600">{addressSearchError}</p>
              )}

              <div className="space-y-2 max-h-96 overflow-y-auto">
                {addressResults.map((result, idx) => (
                  <div
                    key={`${result.road}-${idx}`}
                    className="p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                    onClick={() => selectAddress(result)}
                  >
                    <p className="font-medium">{result.road}</p>
                    <p className="text-sm text-gray-500">
                      우편번호: {result.postalCode || '-'} / 위도: {result.lat}, 경도: {result.lon}
                    </p>
                  </div>
                ))}
              </div>
              
              <p className="mt-4 text-xs text-gray-500">
                * 네이버 지오코딩 API 검색 결과입니다.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Map Modal */}
      {isMapModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold">지도에서 위치 선택</h3>
              <button
                onClick={() => setIsMapModalOpen(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4">
              {!import.meta.env.VITE_NAVER_MAP_CLIENT_ID ? (
                <div className="h-96 bg-yellow-50 rounded-lg flex items-center justify-center border border-yellow-300">
                  <p className="text-sm text-yellow-700">
                    `.env`에 `VITE_NAVER_MAP_CLIENT_ID`를 설정해 주세요.
                  </p>
                </div>
              ) : (
                <div ref={mapModalRef} className="h-96 rounded-lg border border-gray-300" />
              )}
              
              <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>사용 방법:</strong>
                </p>
                <ul className="mt-2 text-sm text-gray-600 space-y-1">
                  <li>• 지도를 클릭하여 위치를 선택하세요</li>
                  <li>• 마우스 휠로 확대/축소가 가능합니다</li>
                  <li>• 선택한 위치는 자동으로 위도/경도가 입력됩니다</li>
                </ul>
                <div className="mt-3 flex items-center justify-between">
                  <p className="text-xs text-gray-500">
                    현재 좌표: {formData.lat || '-'}, {formData.lon || '-'}
                  </p>
                  <button
                    type="button"
                    onClick={() => {
                      if (formData.lat && formData.lon) {
                        handleReverseGeocode(formData.lat, formData.lon);
                      }
                    }}
                    disabled={!formData.lat || !formData.lon || isReverseGeocoding}
                    className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-blue-400"
                  >
                    {isReverseGeocoding ? '주소 변환 중...' : '좌표로 주소 채우기'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reset Confirmation Modal */}
      {showResetConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center mb-4">
              <AlertCircle className="w-6 h-6 text-orange-500 mr-2" />
              <h3 className="text-lg font-semibold">변경사항 초기화</h3>
            </div>
            <p className="text-gray-600 mb-6">
              모든 변경사항을 취소하고 원래 값으로 되돌리시겠습니까?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowResetConfirm(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                취소
              </button>
              <button
                onClick={handleReset}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
              >
                초기화
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShopUpdate;